import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, InputGroup } from 'react-bootstrap';
import apiService from '../services/api';

const SecuritySettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        password: {
            require_special_chars: true,
            require_numbers: true,
            require_uppercase: true,
            require_lowercase: true,
            max_login_attempts: 5,
            account_inactivity_days: 180,
            enable_two_factor: false,
            session_timeout: 60,
            min_length: 8,
            expiry_days: 90
        }
    });

    useEffect(() => {
        loadSecuritySettings();
    }, []);

    const loadSecuritySettings = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getOrganizationDefaultSettingByCode('security_settings');
            if (response.success && response.data) {
                setSettingData(response.data);
                const settings = response.data.value || {};

                setFormData({
                    password: {
                        require_special_chars: settings.password?.require_special_chars !== undefined ? settings.password.require_special_chars : true,
                        require_numbers: settings.password?.require_numbers !== undefined ? settings.password.require_numbers : true,
                        require_uppercase: settings.password?.require_uppercase !== undefined ? settings.password.require_uppercase : true,
                        require_lowercase: settings.password?.require_lowercase !== undefined ? settings.password.require_lowercase : true,
                        max_login_attempts: settings.password?.max_login_attempts || 5,
                        account_inactivity_days: settings.password?.account_inactivity_days || 180,
                        enable_two_factor: settings.password?.enable_two_factor || false,
                        session_timeout: settings.password?.session_timeout || 60,
                        min_length: settings.password?.min_length || 8,
                        expiry_days: settings.password?.expiry_days || 90
                    }
                });
            } else {
                setError(response.error || 'Failed to load security settings');
            }
        } catch (error) {
            console.error('Error loading security settings:', error);
            setError('Failed to load security settings');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSettingChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            password: {
                ...prev.password,
                [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
            }
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        // Validation
        if (formData.password.min_length < 6) {
            setError('Minimum password length cannot be less than 6 characters');
            setSaving(false);
            return;
        }

        if (formData.password.max_login_attempts < 1) {
            setError('Maximum login attempts must be at least 1');
            setSaving(false);
            return;
        }

        if (formData.password.session_timeout < 5) {
            setError('Session timeout must be at least 5 minutes');
            setSaving(false);
            return;
        }

        try {
            const updateData = {
                name: 'Security Settings',
                code: 'security_settings',
                value: formData
            };

            let response;
            if (settingData && settingData.id) {
                // Update existing setting
                response = await apiService.updateOrganizationDefaultSetting(settingData.id, updateData);
            } else {
                // Create new setting
                response = await apiService.createOrganizationDefaultSetting(updateData);
            }

            if (response.success) {
                setSuccess('Security settings saved successfully!');
                setSettingData(response.data);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.error || 'Failed to save security settings');
            }
        } catch (error) {
            console.error('Error saving security settings:', error);
            setError('Failed to save security settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading security settings...</div>
            </div>
        );
    }

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-shield-lock me-2"></i>
                            Security Settings
                        </h2>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            <i className="bi bi-check-circle me-2"></i>
                            {success}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Form onSubmit={handleSave}>
                <Row>
                    <Col lg={8}>
                        {/* Password Policy */}
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="bi bi-key me-2"></i>
                                    Password Policy
                                </h5>
                                <small className="text-muted">Define password requirements for user accounts</small>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Minimum Length</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="min_length"
                                                    value={formData.password.min_length}
                                                    onChange={handlePasswordSettingChange}
                                                    min="6"
                                                    max="50"
                                                    required
                                                />
                                                <InputGroup.Text>characters</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Minimum number of characters required (6-50)
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Password Expiry</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="expiry_days"
                                                    value={formData.password.expiry_days}
                                                    onChange={handlePasswordSettingChange}
                                                    min="1"
                                                    max="365"
                                                    required
                                                />
                                                <InputGroup.Text>days</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Password must be changed every X days (0 = never expires)
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="require_uppercase"
                                                name="require_uppercase"
                                                checked={formData.password.require_uppercase}
                                                onChange={handlePasswordSettingChange}
                                                label="Require Uppercase Letters (A-Z)"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="require_lowercase"
                                                name="require_lowercase"
                                                checked={formData.password.require_lowercase}
                                                onChange={handlePasswordSettingChange}
                                                label="Require Lowercase Letters (a-z)"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="require_numbers"
                                                name="require_numbers"
                                                checked={formData.password.require_numbers}
                                                onChange={handlePasswordSettingChange}
                                                label="Require Numbers (0-9)"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="require_special_chars"
                                                name="require_special_chars"
                                                checked={formData.password.require_special_chars}
                                                onChange={handlePasswordSettingChange}
                                                label="Require Special Characters (!@#$%^&*)"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Account Security */}
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="bi bi-person-lock me-2"></i>
                                    Account Security
                                </h5>
                                <small className="text-muted">Configure account lockout and session policies</small>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Maximum Login Attempts</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="max_login_attempts"
                                                    value={formData.password.max_login_attempts}
                                                    onChange={handlePasswordSettingChange}
                                                    min="1"
                                                    max="20"
                                                    required
                                                />
                                                <InputGroup.Text>attempts</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Account will be locked after X failed attempts
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Account Inactivity Period</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="account_inactivity_days"
                                                    value={formData.password.account_inactivity_days}
                                                    onChange={handlePasswordSettingChange}
                                                    min="1"
                                                    max="999"
                                                    required
                                                />
                                                <InputGroup.Text>days</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Automatically disable accounts after X days of inactivity
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Session Timeout</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="session_timeout"
                                                    value={formData.password.session_timeout}
                                                    onChange={handlePasswordSettingChange}
                                                    min="5"
                                                    max="1440"
                                                    required
                                                />
                                                <InputGroup.Text>minutes</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Automatically log out users after X minutes of inactivity
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="enable_two_factor"
                                                name="enable_two_factor"
                                                checked={formData.password.enable_two_factor}
                                                onChange={handlePasswordSettingChange}
                                                label="Enable Two-Factor Authentication (2FA)"
                                            />
                                            <Form.Text className="text-muted">
                                                Require additional verification for user logins
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="bg-light mb-4">
                            <Card.Header>
                                <h6 className="mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Security Guidelines
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-2">
                                    <strong>Strong Passwords:</strong> Enable all character type requirements for maximum security.
                                </p>
                                <p className="mb-2">
                                    <strong>Account Lockout:</strong> Set reasonable attempt limits to prevent brute force attacks.
                                </p>
                                <p className="mb-0">
                                    <strong>Session Management:</strong> Shorter timeouts increase security but may impact user experience.
                                </p>
                            </Card.Body>
                        </Card>

                        <Card className="border-info mb-4">
                            <Card.Header className="bg-info text-white">
                                <h6 className="mb-0">
                                    <i className="bi bi-lightbulb me-2"></i>
                                    Password Strength Preview
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-2">
                                    <strong>Current Requirements:</strong>
                                </div>
                                <ul className="mb-0 small">
                                    <li>Minimum {formData.password.min_length} characters</li>
                                    {formData.password.require_uppercase && <li>Uppercase letters (A-Z)</li>}
                                    {formData.password.require_lowercase && <li>Lowercase letters (a-z)</li>}
                                    {formData.password.require_numbers && <li>Numbers (0-9)</li>}
                                    {formData.password.require_special_chars && <li>Special characters (!@#$%^&*)</li>}
                                    <li>Expires every {formData.password.expiry_days} days</li>
                                </ul>
                            </Card.Body>
                        </Card>

                        <Card className="border-warning">
                            <Card.Header className="bg-warning text-dark">
                                <h6 className="mb-0">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Important Notes
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <ul className="mb-0">
                                    <li className="mb-2">Changes affect new accounts and password resets</li>
                                    <li className="mb-2">2FA requires SMS or email configuration</li>
                                    <li className="mb-0">Test settings with a non-admin account first</li>
                                </ul>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card>
                            <Card.Footer>
                                <div className="d-flex justify-content-end">
                                    <Button variant="primary" type="submit" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-shield-check me-2"></i>
                                                Save Security Settings
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Footer>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default SecuritySettings;